import {Box, Typography, Button, Paper, alpha} from '@mui/material';
import PageContainer from 'packages/common/components/page-container';
import PropTypes from 'prop-types';
import {ForbiddenIllustration} from 'packages/common/assets/illustrations';
import {RouterLink} from 'packages/common/routes/components';
import {paths} from 'packages/common/routes/paths';

export default function ViewFeatureError({feature, missingDependencies = [], moduleDetails = null, reason = 'subscription'}) {
  // Module désactivé volontairement dans les paramètres de l'établissement
  // (ex. bons cadeaux) : proposer la réactivation plutôt que la souscription
  const isDeactivated = reason === 'deactivated';

  // Messages par défaut pour les modules connus
  const defaultMessages = {
    wellness: {
      module: 'Module Bien-être',
      title: 'Accès non disponible',
      description: 'Vous n\'avez pas accès à cette fonctionnalité. Pour plus d\'informations, contactez le support.',
    },
    employees: {
      module: 'Module Gestion des employés',
      title: 'Accès non disponible',
      description: 'Vous n\'avez pas accès à cette fonctionnalité. Pour plus d\'informations, contactez le support.',
    },
    default: {
      module: 'Module',
      title: 'Accès non disponible',
      description: 'Vous n\'avez pas accès à cette fonctionnalité. Pour plus d\'informations, contactez le support.',
    },
  };

  // Utiliser les détails du module si disponibles, sinon utiliser les messages par défaut
  const currentMessage = moduleDetails ? {
    module: moduleDetails.name || `Module ${feature}`,
    title: isDeactivated ? 'Module désactivé' : 'Accès non disponible',
    description: moduleDetails.description || (isDeactivated ?
      'Ce module a été désactivé dans les paramètres de votre établissement.' :
      'Vous n\'avez pas accès à cette fonctionnalité. Pour plus d\'informations, contactez le support.'),
  } : (defaultMessages[feature] || defaultMessages.default);

  return (
    <Box
      sx={{
        height: 1,
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.neutral',
      }}
    >
      <PageContainer maxWidth="lg">
        <Paper
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'},
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: {xs: 4, md: 8},
            p: {xs: 3, md: 6},
            bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'background.neutral',
            borderRadius: 3,
            boxShadow: (theme) => `0 0 24px ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
          elevation={0}
        >
          {/* Section Gauche - Contenu */}
          <Box sx={{flex: 1, maxWidth: 480}}>
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                mb: 1,
                fontWeight: 500,
              }}
            >
              {currentMessage.module}
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              {currentMessage.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              {currentMessage.description}
            </Typography>

            {/* Card premium minimal et raffinée */}
            <Box
              sx={{
                'border': '1px solid',
                'borderColor': 'divider',
                'borderRadius': 1,
                'p': 3,
                'mb': 4,
                'backgroundColor': 'background.paper',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  mb: 1.5,
                }}
              >
                {isDeactivated ? 'Module désactivé' : 'Accès Premium'}
              </Typography>

              {isDeactivated ? (
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    lineHeight: 1.6,
                    mb: 0,
                  }}
                >
                  <strong>{moduleDetails?.name || feature}</strong> a été désactivé dans les paramètres
                  de votre établissement. Vos données sont conservées : réactivez le module à tout
                  moment pour retrouver l&apos;ensemble des fonctionnalités.
                </Typography>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    lineHeight: 1.6,
                    mb: 0,
                  }}
                >
                  <strong>{moduleDetails?.name || feature}</strong> nécessite un abonnement pour être utilisé.
                  Cette fonctionnalité fait partie de notre suite d&apos;outils professionnels avancés.
                </Typography>
              )}
            </Box>

            {missingDependencies.length > 0 && (
              <Box sx={{mb: 4}}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'text.primary',
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Modules requis pour accéder à cette fonctionnalité
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  {missingDependencies.map((dep) => (
                    <Box
                      key={dep}
                      sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.04),
                        border: '1px solid',
                        borderColor: 'error.light',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                          mr: 1.5,
                        }}
                      />
                      <Typography
                        sx={{
                          color: 'error.dark',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                        }}
                      >
                        {defaultMessages[dep]?.module || dep}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Actions avec design sobre et professionnel */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row'},
                gap: 2,
                alignItems: 'stretch',
                width: '100%',
                maxWidth: 500,
              }}
            >
              <Button
                component={RouterLink}
                href={paths.app.settings.payment.modules}
                size="large"
                variant="contained"
                sx={{
                  'flex': 1,
                  'py': 1.5,
                  'px': 3,
                  'fontWeight': 600,
                  'textTransform': 'none',
                  'borderRadius': 1.5,
                  'whiteSpace': 'nowrap',
                  'boxShadow': (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                  '&:hover': {
                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                  },
                }}
              >
                {isDeactivated ? 'Réactiver dans les paramètres' : 'Souscrire un abonnement'}
              </Button>

              <Button
                component={RouterLink}
                href={paths.app.dashboard}
                size="large"
                variant="outlined"
                sx={{
                  'flex': 1,
                  'py': 1.5,
                  'px': 3,
                  'fontWeight': 600,
                  'textTransform': 'none',
                  'borderRadius': 1.5,
                  'whiteSpace': 'nowrap',
                  'borderColor': 'grey.300',
                  'color': 'text.secondary',
                  '&:hover': {
                    borderColor: 'grey.400',
                    backgroundColor: 'grey.50',
                    color: 'text.primary',
                  },
                }}
              >
                Retour au tableau de bord
              </Button>
            </Box>
          </Box>

          {/* Section Droite - Illustration */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ForbiddenIllustration
              sx={{
                width: '100%',
                maxWidth: 400,
                opacity: 0.9,
              }}
            />
          </Box>
        </Paper>
      </PageContainer>
    </Box>
  );
}

ViewFeatureError.propTypes = {
  feature: PropTypes.string.isRequired,
  missingDependencies: PropTypes.arrayOf(PropTypes.string),
  moduleDetails: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    key: PropTypes.string,
    category: PropTypes.string,
  }),
  reason: PropTypes.oneOf(['subscription', 'deactivated']),
};
